import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { dbGetAll, dbPut, dbDelete, dbClear } from '../utils/db';
import {
  DEFAULT_SPACE_VIEW,
  PREFERENCES_STORAGE_KEY,
  SYNC_SETTINGS_STORAGE_KEY,
  SYNC_STATE_STORAGE_KEY,
  SYNC_THROTTLE_MINUTES,
  SESSION_STORAGE_KEY,
  CLIENT_ID_STORAGE_KEY,
  SCRIPT_WEB_APP_URL_KEY,
  DEFAULT_SCRIPT_WEB_APP_URL
} from '../constants';

const StoreContext = createContext();
export const useStore = () => useContext(StoreContext);

function toFormBody(obj) {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'string') p.append(k, v);
    else p.append(k, JSON.stringify(v));
  });
  return p.toString();
}

function normalizeScriptUrl(raw) {
  const s = String(raw || '').trim().replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
  if (!s) return '';
  let url = s;
  if (url.includes('/dev')) url = url.replace('/dev', '/exec');
  if (url.includes('script.google.com') && !url.endsWith('/exec')) url = url.replace(/\/+$/, '') + '/exec';
  return url;
}

function toEpoch(value) {
  if (!value) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (/^\d+$/.test(String(value))) return Number(value);
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function forServer(record) {
  if (!record) return record;
  const updatedMs = toEpoch(record.updatedAt) || Date.now();
  return {
    ...record,
    updatedAt: new Date(updatedMs).toISOString(),
    createdAt: record.createdAt ? new Date(toEpoch(record.createdAt)).toISOString() : record.createdAt,
    deletedAt: record.deletedAt ? new Date(toEpoch(record.deletedAt)).toISOString() : null,
    archivedAt: record.archivedAt ? new Date(toEpoch(record.archivedAt)).toISOString() : null
  };
}

function forLocal(record) {
  if (!record) return record;
  return {
    ...record,
    updatedAt: toEpoch(record.updatedAt) || Date.now(),
    createdAt: record.createdAt ? toEpoch(record.createdAt) : record.createdAt,
    deletedAt: record.deletedAt ? toEpoch(record.deletedAt) : null,
    archivedAt: record.archivedAt ? toEpoch(record.archivedAt) : null
  };
}

export const StoreProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [notes, setNotes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [backups, setBackups] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState('ws-default');
  const [viewMode, setViewMode] = useState(DEFAULT_SPACE_VIEW);
  const [focusMode, setFocusMode] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [showArchivedWorkspaces, setShowArchivedWorkspaces] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [isSyncing, setIsSyncing] = useState(false);

  const syncTimerRef = useRef(null);
  const syncLockRef = useRef(false);

  const [clientId] = useState(() => {
    let id = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem(CLIENT_ID_STORAGE_KEY, id);
    }
    return id;
  });

  const [sessionToken, setSessionToken] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);

  const [confirmState, setConfirmState] = useState({
    open: false, title: '', message: '', danger: false, onConfirm: null,
    confirmLabel: 'Confirm', cancelLabel: 'Cancel'
  });

  const openConfirm = (options) => setConfirmState({
    open: true,
    title: options.title || 'Are you sure?',
    message: options.message || '',
    danger: options.danger || false,
    onConfirm: options.onConfirm,
    confirmLabel: options.confirmLabel || 'Confirm',
    cancelLabel: options.cancelLabel || 'Cancel'
  });
  const closeConfirm = () => setConfirmState(prev => ({ ...prev, open: false }));

  const [syncSettings, setSyncSettings] = useState(() => {
    const saved = localStorage.getItem(SYNC_SETTINGS_STORAGE_KEY);
    let parsed = {};
    try { parsed = saved ? JSON.parse(saved) : {}; } catch { parsed = {}; }
    const storedUrl = localStorage.getItem(SCRIPT_WEB_APP_URL_KEY);
    const scriptUrl = normalizeScriptUrl(storedUrl || parsed.scriptUrl || DEFAULT_SCRIPT_WEB_APP_URL);
    if (!storedUrl && scriptUrl) localStorage.setItem(SCRIPT_WEB_APP_URL_KEY, scriptUrl);
    return { scriptUrl, apiKey: parsed.apiKey || '', autoSync: parsed.autoSync !== false };
  });

  const updateSyncSettings = (updates) => {
    setSyncSettings(prev => {
      const next = { ...prev, ...updates };
      if (updates.scriptUrl !== undefined) {
        next.scriptUrl = normalizeScriptUrl(updates.scriptUrl);
        localStorage.setItem(SCRIPT_WEB_APP_URL_KEY, next.scriptUrl);
      }
      localStorage.setItem(SYNC_SETTINGS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const [syncState, setSyncState] = useState(() => {
    const saved = localStorage.getItem(SYNC_STATE_STORAGE_KEY);
    try {
      const parsed = saved ? JSON.parse(saved) : {};
      return {
        lastSyncAt: toEpoch(parsed.lastSyncAt),
        lastSyncToken: parsed.lastSyncToken || '',
        status: parsed.status || 'idle',
        error: parsed.error || null
      };
    } catch {
      return { lastSyncAt: 0, lastSyncToken: '', status: 'idle', error: null };
    }
  });

  const persistSyncState = (next) => {
    setSyncState(next);
    localStorage.setItem(SYNC_STATE_STORAGE_KEY, JSON.stringify(next));
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionToken(null);
    setSessionExpiresAt(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  const checkSessionExpiry = useCallback(() => {
    if (sessionExpiresAt && toEpoch(sessionExpiresAt) <= Date.now()) clearSession();
  }, [sessionExpiresAt, clearSession]);

  const testScriptConnection = async (url) => {
    const target = normalizeScriptUrl(url || syncSettings.scriptUrl || DEFAULT_SCRIPT_WEB_APP_URL);
    if (!target) throw new Error('Missing Apps Script URL');
    try {
      const res = await fetch(target, { method: 'GET', redirect: 'follow' });
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { throw new Error(`Non-JSON response: ${text.slice(0, 160)}`); }
      if (!res.ok || json?.ok !== true) throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      return json;
    } catch (err) {
      throw new Error(err.message || 'Network error');
    }
  };

  const login = async (username, passcode) => {
    setAuthLoading(true);
    setAuthError(null);
    const scriptUrl = normalizeScriptUrl(syncSettings.scriptUrl || DEFAULT_SCRIPT_WEB_APP_URL);
    if (!scriptUrl) {
      setAuthError('MISSING_SCRIPT_URL');
      setAuthLoading(false);
      return false;
    }

    try {
      const body = toFormBody({ action: 'login', username, passcode, clientId });
      const res = await fetch(scriptUrl, {
        method: 'POST', mode: 'cors', redirect: 'follow',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }, body
      });
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { throw new Error(`Non-JSON response: ${text.slice(0, 240)}`); }
      if (!res.ok || !json.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);

      const sessionData = {
        token: json.sessionToken,
        expiresAt: json.sessionExpiresAt,
        username: json.username || username
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      setSessionToken(json.sessionToken);
      setSessionExpiresAt(json.sessionExpiresAt);
      setCurrentUser(json.username || username);
      setIsAuthenticated(true);
      setAuthLoading(false);
      return true;
    } catch (err) {
      setAuthError(err.message);
      setAuthLoading(false);
      return false;
    }
  };

  const logout = () => clearSession();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ws, nts, tmpl, bks] = await Promise.all([
        dbGetAll('workspaces'), dbGetAll('notes'), dbGetAll('templates'), dbGetAll('backups')
      ]);
      setWorkspaces(ws.map(forLocal));
      setNotes(nts.map(forLocal));
      setTemplates(tmpl.map(forLocal));
      setBackups(bks.sort((a, b) => b.createdAt - a.createdAt));
    } catch (err) {
      console.error('Load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        const { token, expiresAt, username } = JSON.parse(saved);
        if (token && expiresAt && toEpoch(expiresAt) > Date.now()) {
          setSessionToken(token);
          setSessionExpiresAt(expiresAt);
          setCurrentUser(username);
          setIsAuthenticated(true);
        } else if (saved) {
          localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
    } catch {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
    loadData();
  }, [loadData]);

  const mergeRemote = async (store, local, remote) => {
    const map = new Map(local.map(item => [item.id, item]));
    for (const raw of remote || []) {
      const r = forLocal(raw);
      const l = map.get(r.id);
      if (!l || toEpoch(r.updatedAt) > toEpoch(l.updatedAt)) {
        map.set(r.id, r);
        await dbPut(store, r);
      }
    }
    return Array.from(map.values());
  };

  const runSync = useCallback(async (trigger = 'manual') => {
    const scriptUrl = normalizeScriptUrl(syncSettings.scriptUrl || DEFAULT_SCRIPT_WEB_APP_URL);
    if (!isAuthenticated || !sessionToken || !scriptUrl || syncLockRef.current) return false;
    if (!navigator.onLine) {
      setSaveStatus('offline');
      return false;
    }

    if (trigger === 'activation' && syncState.lastSyncAt) {
      const diff = (Date.now() - syncState.lastSyncAt) / 60000;
      if (diff < SYNC_THROTTLE_MINUTES) return false;
    }

    syncLockRef.current = true;
    setIsSyncing(true);
    setSaveStatus('syncing');
    setSyncState(prev => ({ ...prev, status: 'syncing', error: null }));

    try {
      // Read the persisted local database at sync time so we never push stale React state.
      const [dbWs, dbNotes, dbTemplates] = await Promise.all([
        dbGetAll('workspaces'), dbGetAll('notes'), dbGetAll('templates')
      ]);
      const lastPush = syncState.lastSyncAt || 0;
      const changedSince = (item) => !lastPush || toEpoch(item.updatedAt) > lastPush;
      const payload = {
        workspaces: dbWs.filter(changedSince).map(forServer),
        notes: dbNotes.filter(changedSince).map(forServer),
        templates: dbTemplates.filter(changedSince).map(forServer)
      };

      const body = toFormBody({
        action: 'sync', sessionToken, clientId, apiKey: syncSettings.apiKey,
        mode: 'two_way', since: syncState.lastSyncToken || '', payload
      });

      const res = await fetch(scriptUrl, {
        method: 'POST', mode: 'cors', redirect: 'follow',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }, body
      });
      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { throw new Error(`Non-JSON response: ${text.slice(0, 240)}`); }
      if (!res.ok || !result.ok) {
        const reason = result.error || result.message || `HTTP ${res.status}`;
        if (/session expired|unauthorized/i.test(reason)) clearSession();
        throw new Error(reason);
      }

      const nextWorkspaces = await mergeRemote('workspaces', dbWs.map(forLocal), result.changes?.workspaces || []);
      const nextNotes = await mergeRemote('notes', dbNotes.map(forLocal), result.changes?.notes || []);
      const nextTemplates = await mergeRemote('templates', dbTemplates.map(forLocal), result.changes?.templates || []);
      setWorkspaces(nextWorkspaces);
      setNotes(nextNotes);
      setTemplates(nextTemplates);

      const token = result.syncToken || result.serverTime || new Date().toISOString();
      const nextState = {
        lastSyncAt: toEpoch(token) || Date.now(),
        lastSyncToken: String(token),
        status: 'success', error: null
      };
      persistSyncState(nextState);
      setSaveStatus('synced');
      return true;
    } catch (err) {
      console.error('STICKYHQ_SYNC_FAIL', err);
      const next = { ...syncState, status: 'error', error: err.message };
      persistSyncState(next);
      setSaveStatus('error');
      return false;
    } finally {
      syncLockRef.current = false;
      setIsSyncing(false);
    }
  }, [clearSession, clientId, isAuthenticated, sessionToken, syncSettings, syncState]);

  const scheduleSync = useCallback((delay = 900) => {
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => runSync('autosave'), delay);
  }, [runSync]);

  useEffect(() => () => clearTimeout(syncTimerRef.current), []);

  const saveNote = async (note, isAutosave = false) => {
    const updated = { ...note, updatedAt: Date.now(), rev: (note.rev || 0) + 1 };
    setSaveStatus('saving');
    try {
      await dbPut('notes', updated);
      setNotes(prev => prev.map(n => n.id === note.id ? updated : n));
      setEditingNote(prev => prev?.id === updated.id ? updated : prev);
      setSaveStatus('saved');
      if (isAutosave) scheduleSync(1000);
      return updated;
    } catch (err) {
      console.error('Save failed', err);
      setSaveStatus('error');
      throw err;
    }
  };

  const createNote = (type = 'text', overrides = {}) => {
    const now = Date.now();
    const newNote = {
      id: `note-${now}-${Math.random().toString(36).substr(2, 9)}`,
      workspaceId: activeWorkspaceId,
      title: '', type, bodyText: '', checklistItems: [],
      table: type === 'table' ? { columns: [], rows: [] } : undefined,
      tags: [], pinned: false, rank: 1, colorKey: 'white', mentions: [], backlinks: [], attachments: [],
      createdAt: now, updatedAt: now, deletedAt: null, archivedAt: null, rev: 1,
      ...overrides
    };
    dbPut('notes', newNote).then(() => scheduleSync());
    setNotes(prev => [newNote, ...prev]);
    setEditingNote(newNote);
    return newNote;
  };

  const updateNoteAndSync = async (id, mutator) => {
    const source = notes.find(n => n.id === id);
    if (!source) return;
    const updated = { ...mutator(source), updatedAt: Date.now(), rev: (source.rev || 0) + 1 };
    await dbPut('notes', updated);
    setNotes(prev => prev.map(n => n.id === id ? updated : n));
    setEditingNote(prev => prev?.id === id ? updated : prev);
    scheduleSync();
    return updated;
  };

  const togglePin = (note) => updateNoteAndSync(note.id, n => ({ ...n, pinned: !n.pinned }));
  const archiveNote = (id, unarchive = false) => updateNoteAndSync(id, n => ({ ...n, archivedAt: unarchive ? null : Date.now() }));
  const softDeleteNote = (id) => updateNoteAndSync(id, n => ({ ...n, deletedAt: Date.now() }));
  const restoreNote = (id) => updateNoteAndSync(id, n => ({ ...n, deletedAt: null }));

  const deleteNotePermanently = async (id) => {
    const note = notes.find(n => n.id === id);
    // Preserve a tombstone for cloud propagation instead of silently deleting only locally.
    if (note) await updateNoteAndSync(id, n => ({ ...n, deletedAt: Date.now(), permanentlyDeleted: true }));
    setSelectedNoteIds(prev => prev.filter(iid => iid !== id));
  };

  const createWorkspace = async (name, color = '#3B82F6') => {
    const now = Date.now();
    const newWs = { id: `ws-${now}`, name, color, createdAt: now, updatedAt: now, archivedAt: null };
    await dbPut('workspaces', newWs);
    setWorkspaces(prev => [...prev, newWs]);
    setActiveWorkspaceId(newWs.id);
    scheduleSync();
    return newWs;
  };

  const renameWorkspace = async (id, name) => {
    const ws = workspaces.find(w => w.id === id);
    if (!ws) return;
    const updated = { ...ws, name, updatedAt: Date.now() };
    await dbPut('workspaces', updated);
    setWorkspaces(prev => prev.map(w => w.id === id ? updated : w));
    scheduleSync();
  };

  const deleteWorkspace = async (id) => {
    const ws = workspaces.find(w => w.id === id);
    if (!ws) return;
    const updated = { ...ws, deletedAt: Date.now(), updatedAt: Date.now() };
    await dbPut('workspaces', updated);
    setWorkspaces(prev => prev.map(w => w.id === id ? updated : w));
    if (activeWorkspaceId === id) setActiveWorkspaceId('ws-default');
    scheduleSync();
  };

  const archiveWorkspace = async (id, unarchive = false) => {
    const ws = workspaces.find(w => w.id === id);
    if (!ws) return;
    const updated = { ...ws, archivedAt: unarchive ? null : Date.now(), updatedAt: Date.now(), rev: (ws.rev || 0) + 1 };
    await dbPut('workspaces', updated);
    setWorkspaces(prev => prev.map(w => w.id === id ? updated : w));
    scheduleSync();
  };

  const createTemplate = async (template) => {
    const now = Date.now();
    const newTmpl = { id: `tmpl-${now}`, createdAt: now, updatedAt: now, ...template };
    await dbPut('templates', newTmpl);
    setTemplates(prev => [...prev, newTmpl]);
    scheduleSync();
    return newTmpl;
  };

  const updateTemplate = async (updated) => {
    const final = { ...updated, updatedAt: Date.now() };
    await dbPut('templates', final);
    setTemplates(prev => prev.map(t => t.id === updated.id ? final : t));
    scheduleSync();
  };

  const deleteTemplate = async (id) => {
    const tmpl = templates.find(t => t.id === id);
    if (!tmpl) return;
    const deleted = { ...tmpl, deletedAt: Date.now(), updatedAt: Date.now() };
    await dbPut('templates', deleted);
    setTemplates(prev => prev.map(t => t.id === id ? deleted : t));
    scheduleSync();
  };

  const createNoteFromTemplate = (template) => createNote(template.type, {
    title: template.presetTitle || '', tags: template.presetTags || [],
    colorKey: template.presetColorKey || 'white', bodyText: template.content || '',
    checklistItems: template.checklistItems || [], table: template.table
  });

  const bulkUpdate = async (ids, mutator) => {
    const now = Date.now();
    const changed = notes.map(n => ids.includes(n.id) ? { ...mutator(n), updatedAt: now, rev: (n.rev || 0) + 1 } : n);
    await Promise.all(changed.filter(n => ids.includes(n.id)).map(n => dbPut('notes', n)));
    setNotes(changed);
    scheduleSync();
  };

  const bulkArchive = async (ids) => { await bulkUpdate(ids, n => ({ ...n, archivedAt: Date.now() })); setSelectedNoteIds([]); };
  const bulkSetColor = (ids, colorKey) => bulkUpdate(ids, n => ({ ...n, colorKey }));
  const bulkAddTag = (ids, tagName) => {
    const tag = tagName.replace(/^#/, '').trim();
    if (!tag) return;
    return bulkUpdate(ids, n => ({ ...n, tags: n.tags?.includes(tag) ? n.tags : [...(n.tags || []), tag] }));
  };
  const bulkRemoveTag = (ids, tag) => bulkUpdate(ids, n => ({ ...n, tags: (n.tags || []).filter(t => t !== tag) }));
  const bulkDeletePermanently = async (ids) => { await bulkUpdate(ids, n => ({ ...n, deletedAt: Date.now(), permanentlyDeleted: true })); setSelectedNoteIds([]); };

  const createBackup = async () => {
    const snapshot = { id: `backup-${Date.now()}`, createdAt: Date.now(), data: { workspaces, notes, templates } };
    await dbPut('backups', snapshot);
    setBackups(prev => [snapshot, ...prev]);
  };

  const restoreBackup = async (backup) => {
    await dbClear('workspaces'); await dbClear('notes'); await dbClear('templates');
    await Promise.all([
      ...backup.data.workspaces.map(w => dbPut('workspaces', w)),
      ...backup.data.notes.map(n => dbPut('notes', n)),
      ...backup.data.templates.map(t => dbPut('templates', t))
    ]);
    await loadData();
    scheduleSync();
  };

  const deleteBackup = async (id) => { await dbDelete('backups', id); setBackups(prev => prev.filter(b => b.id !== id)); };

  const exportAllData = () => {
    const data = { exportedAt: new Date().toISOString(), workspaces, notes, templates, backups };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `stickyhq-export-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const importData = async (file, mode = 'merge') => {
    const text = await file.text();
    const data = JSON.parse(text);
    if (mode === 'replace') { await dbClear('workspaces'); await dbClear('notes'); await dbClear('templates'); }
    await Promise.all([
      ...(data.workspaces || []).map(w => dbPut('workspaces', forLocal(w))),
      ...(data.notes || []).map(n => dbPut('notes', forLocal(n))),
      ...(data.templates || []).map(t => dbPut('templates', forLocal(t)))
    ]);
    await loadData();
    scheduleSync();
  };

  const resetPreferences = () => { localStorage.removeItem(PREFERENCES_STORAGE_KEY); setViewMode(DEFAULT_SPACE_VIEW); };

  const val = {
    workspaces, notes, templates: templates.filter(t => !t.deletedAt), backups,
    activeWorkspaceId, setActiveWorkspaceId, viewMode, setViewMode, focusMode, setFocusMode,
    isPaletteOpen, setIsPaletteOpen, editingNote, setEditingNote, saveNote, createNote, togglePin,
    archiveNote, softDeleteNote, restoreNote, deleteNotePermanently, createWorkspace, renameWorkspace,
    deleteWorkspace, createTemplate, updateTemplate, deleteTemplate, createNoteFromTemplate,
    bulkArchive, bulkSetColor, bulkAddTag, bulkRemoveTag, bulkDeletePermanently,
    createBackup, restoreBackup, deleteBackup, exportAllData, importData, resetPreferences,
    archiveWorkspace, selectedNoteIds, setSelectedNoteIds, showArchived, setShowArchived,
    showArchivedWorkspaces, setShowArchivedWorkspaces, syncSettings, setSyncSettings: updateSyncSettings,
    syncState, runSync, isSyncing, confirmState, openConfirm, closeConfirm,
    isAuthenticated, currentUser, login, logout, authError, authLoading, checkSessionExpiry,
    loading, testScriptConnection, saveStatus, setSaveStatus
  };

  return <StoreContext.Provider value={val}>{children}</StoreContext.Provider>;
};